/**
 * Meta pixel base script for the app.
 *
 * `conversion.ts` fires `fbq('track', 'CompleteRegistration')` at signup but
 * only `if (fbq)`. Nothing in the repo ever defined `window.fbq`, so that
 * branch was dead and Meta has never been told about a single signup. This is
 * the missing half.
 *
 * The base snippet defines `window.fbq` synchronously as a queue and only then
 * loads fbevents.js, so a CompleteRegistration fired before the network script
 * arrives is queued rather than dropped. That matters here: signup can happen
 * seconds after load.
 *
 * No-ops when VITE_META_PIXEL_ID is unset, so local and preview builds send
 * Meta nothing.
 */

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

type PixelWindow = Window & { fbq?: Fbq; _fbq?: Fbq };

export function initMetaPixel() {
  if (!PIXEL_ID) {
    return;
  }

  try {
    const w = window as PixelWindow;

    if (w.fbq) {
      return;
    }

    const fbq: Fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        // Meta's snippet uses .apply(fbq, args); spread with a member call
        // binds `this` to fbq identically, and satisfies prefer-spread.
        fbq.callMethod(...args);
      } else {
        fbq.queue?.push(args);
      }
    } as Fbq;

    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';

    w.fbq = fbq;
    w._fbq ??= fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);

    fbq('init', PIXEL_ID);
    fbq('track', 'PageView');
  } catch {
    // Analytics must never break the app.
  }
}
